export const name = 'queryUrlCreator';
export const description = 'Generates a query URL for statistical charts based on the specified filter criteria and the desired charts to view. Returns the URL on success. IMPORTANT: Before adding any company name to the "companies" parameter list, you MUST call the findExactCompanyName tool first to search for and verify the exact official company name.';

export const parameters = {
  isDateRange: 'Optional. True if filtering by a date range (interval), False or omitted if filtering by a specific date.',
  dateValue: 'Optional. Date of a specific day in YYYY-MM-DD format (e.g. "2026-06-18"). Required only if isDateRange is false.',
  startDate: 'Optional. Start date of the range in YYYY-MM-DD format. Required only if isDateRange is true.',
  endDate: 'Optional. End date of the range in YYYY-MM-DD format. Required only if isDateRange is true.',
  companies: 'Optional. Array of company names to filter by, e.g. ["Gipsz Jakab Kft"].',
  projectNumbers: 'Optional. Array of project numbers to filter by, e.g. ["M26-001"].',
  datatables: 'Optional. Array of database table names to filter by, e.g. ["Anyag", "Fuvardíj"].',
  graphs: 'Optional. Array of keys of the charts/graphs to display. Allowed values: "costs", "costs_by_company", "costs_by_company_daily", "costs_by_company_weekly", "costs_by_company_monthly", "costs_by_company_yearly", "costs_by_datatable", "costs_by_datatable_daily", "costs_by_datatable_weekly", "costs_by_datatable_monthly", "costs_by_datatable_yearly", "costs_by_project", "costs_by_project_daily", "costs_by_project_weekly", "costs_by_project_monthly", "costs_by_project_yearly", "incomes", "incomes_by_customer", "incomes_by_customer_weekly", "incomes_by_customer_monthly", "incomes_by_customer_yearly", "incomes_by_project", "incomes_by_project_weekly", "incomes_by_project_monthly", "incomes_by_project_yearly", "results", "results_monthly", "results_yearly", "results_by_customer", "results_by_customer_weekly", "results_by_customer_monthly", "results_by_customer_yearly", "results_by_project", "results_by_project_weekly", "results_by_project_monthly", "results_by_project_yearly".'
};

export const outputTextForUI = 'Lekérdezés összeállítása';
export const useOnMobile = true;

const graphIdMap: Record<string, number> = {
  "costs": 1,
  "costs_by_company": 2,
  "costs_by_company_daily": 3,
  "costs_by_company_weekly": 4,
  "costs_by_company_monthly": 5,
  "costs_by_company_yearly": 6,
  "costs_by_datatable": 7,
  "costs_by_datatable_daily": 8,
  "costs_by_datatable_weekly": 9,
  "costs_by_datatable_monthly": 10,
  "costs_by_datatable_yearly": 11,
  "costs_by_project": 12,
  "costs_by_project_daily": 13,
  "costs_by_project_weekly": 14,
  "costs_by_project_monthly": 15,
  "costs_by_project_yearly": 16,
  "incomes": 17,
  "incomes_by_customer": 18,
  "incomes_by_customer_weekly": 19,
  "incomes_by_customer_monthly": 20,
  "incomes_by_customer_yearly": 21,
  "incomes_by_project": 22,
  "incomes_by_project_weekly": 23,
  "incomes_by_project_monthly": 24,
  "incomes_by_project_yearly": 25,
  "results": 26,
  "results_monthly": 27,
  "results_yearly": 28,
  "results_by_customer": 29,
  "results_by_customer_weekly": 30,
  "results_by_customer_monthly": 31,
  "results_by_customer_yearly": 32,
  "results_by_project": 33,
  "results_by_project_weekly": 34,
  "results_by_project_monthly": 35,
  "results_by_project_yearly": 36
};

export function queryUrlCreator(args: {
  isDateRange?: boolean;
  dateValue?: string;
  startDate?: string;
  endDate?: string;
  companies?: string[];
  projectNumbers?: string[];
  datatables?: string[];
  graphs?: (string | number)[];
}) {
  const isDateRange = !!args.isDateRange;

  let dateRangeValueStr = 'null,null';
  let dateValueStr = 'null';

  const formatToLocale = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'null';
    return d.toLocaleDateString();
  };

  if (isDateRange) {
    const start = args.startDate ? formatToLocale(args.startDate) : 'null';
    const end = args.endDate ? formatToLocale(args.endDate) : 'null';
    dateRangeValueStr = `${start},${end}`;
  } else if (args.dateValue) {
    dateValueStr = formatToLocale(args.dateValue);
  }

  const encodedCompanies = (args.companies || []).map((c) => encodeURIComponent(c)).join(',');
  const encodedProjectNumbers = (args.projectNumbers || []).map((p) => encodeURIComponent(p)).join(',');
  const encodedDatatables = (args.datatables || []).map((d) => encodeURIComponent(d)).join(',');

  // Maps parent-level IDs (section headings) to their child sub-view IDs.
  // Without the children, QueryGraphs.tsx only renders headings but no graph cards.
  const parentToChildren: Record<number, number[]> = {
    1: [2, 7, 12],          // costs → costs_by_company, costs_by_datatable, costs_by_project
    2: [3, 4, 5, 6],        // costs_by_company → daily, weekly, monthly, yearly
    7: [8, 9, 10, 11],      // costs_by_datatable → daily, weekly, monthly, yearly
    12: [13, 14, 15, 16],   // costs_by_project → daily, weekly, monthly, yearly
    17: [18, 22],            // incomes → incomes_by_customer, incomes_by_project
    18: [19, 20, 21],       // incomes_by_customer → weekly, monthly, yearly
    22: [23, 24, 25],       // incomes_by_project → weekly, monthly, yearly
    26: [27, 28, 29, 33],   // results → monthly, yearly, by_customer, by_project
    29: [30, 31, 32],       // results_by_customer → weekly, monthly, yearly
    33: [34, 35, 36],       // results_by_project → weekly, monthly, yearly
  };

  const selectedIds: number[] = [];
  if (args.graphs && Array.isArray(args.graphs)) {
    args.graphs.forEach((g) => {
      if (typeof g === 'number') {
        selectedIds.push(g);
      } else if (typeof g === 'string') {
        const id = graphIdMap[g.toLowerCase()];
        if (id !== undefined) {
          selectedIds.push(id);
        } else {
          const parsed = parseInt(g, 10);
          if (!isNaN(parsed)) {
            selectedIds.push(parsed);
          }
        }
      }
    });
  }

  // Expand parent IDs: when a parent-level graph is selected, automatically
  // include all its children so the page renders actual graph cards, not just headings.
  const expandedIds = new Set<number>(selectedIds);
  selectedIds.forEach((id) => {
    const children = parentToChildren[id];
    if (children) {
      children.forEach((childId) => expandedIds.add(childId));
    }
  });

  const treeViewSelectedIdsStr = Array.from(expandedIds).sort((a, b) => a - b).join(',');

  const queryParams = [
    `isDateRange=${isDateRange}`,
    `dateRangeValue=${dateRangeValueStr}`,
    `dateValue=${dateValueStr}`,
    `companysListValue=${encodedCompanies}`,
    `projectNumbersListValue=${encodedProjectNumbers}`,
    `datatablesListValue=${encodedDatatables}`,
    `treeViewSelectedIds=${treeViewSelectedIdsStr}`
  ].join('&');

  const finalUrl = `/statistics/query-graphs/?${queryParams}`;

  return {
    success: true,
    url: finalUrl
  };
}
